import { useRef, useEffect } from 'react';
import { Box, FormGroup, FormLabel, InputAdornment, TextField } from '@mui/material';

function NumberInput({
  label,
  name,
  value,             // initial value
  defaultValue = 0,  // fallback
  onCommit,          // called on blur with final value
  disabled = false,
  unit,
  startSymbol,
  isCurrency = false,
  allowNegatives = true,
}) {
  const inputRef = useRef();
  const initialValueRef = useRef(value ?? defaultValue);

  // Initialize ref with existing value
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = value ?? defaultValue;
      initialValueRef.current = value ?? defaultValue;
    }
  }, [value, defaultValue]);

  // Only validate/normalize on blur
  const handleBlur = () => {
    let val = inputRef.current.value;

    // Empty or lone "-" -> 0
    if (val === '' || val === '-') val = '0';

    // Normalize currency decimals
    if (isCurrency) val = parseFloat(val).toFixed(2);

    inputRef.current.value = val;

    // Commit only if value changed
    if (onCommit && Number(val) !== Number(initialValueRef.current)) {
      onCommit({
        target: { name, value: val },
        type: 'blur',
      });
    }

    initialValueRef.current = val;
  };

  // Only allow numeric input while typing
  const handleChange = (e) => {
    const val = e.target.value;
    const regex = allowNegatives ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (regex.test(val)) inputRef.current.value = val;
  };

  return (
    <FormGroup>
      {label && <FormLabel>{label}</FormLabel>}
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <TextField
          type="text"
          size="small"
          disabled={disabled}
          name={name}
          defaultValue={value ?? defaultValue}
          inputRef={inputRef}
          onChange={handleChange}
          onBlur={handleBlur}
          InputProps={{
            startAdornment: startSymbol ? <InputAdornment position="start">{startSymbol}</InputAdornment> : null,
            endAdornment: unit ? <InputAdornment position="end">{unit}</InputAdornment> : null,
          }}
        />
      </Box>
    </FormGroup>
  );
}

export default NumberInput;
